import React, { useRef, useEffect, createContext } from 'react';
import { Typography, Box, IconButton,Button, Chip, BDSProvider  } from '@barracuda-internal/bds-core-v2';
import { Dialog } from '@material-ui/core';
import Close from '@mui/icons-material/Close';
import useStyles from 'src/styles/di-theme';
import classNames from 'classnames';
import { getTagStyle } from './Renderer';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import { Feed, Tag } from 'src/types/dataTypes';
import useAPIEffect from 'src/lib/useAPIEffect';
import ImageToolbox from './ImageToolbar';
import CheckIcon from '@mui/icons-material/Check';
import ImageViewer from './ImageViewer';
import { DIHtmlTooltip } from 'src/components/TooltipColumn';
import InfoIcon from '@material-ui/icons/Info';
import FeedbackPopup from './FeedbackPopup';
import FalseNegativeFeedbackPopup from './FalseNegativeFeedbackPopup';
import FeedbackConfirm from './FeedbackConfirm';
import FeedbackIcon from '@mui/icons-material/Feedback';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import "react-image-crop/dist/ReactCrop.css";
import Tooltip from '@mui/material/Tooltip';
import useDetectionStyles from './styles/detection';
import useToast from 'src/hooks/useToast';
import ToastComponent from 'src/components/toast/ToastComponent';
import { setPageTitle } from 'src/features/masthead/mastheadSlice';
import { useAppDispatch } from 'src/hooks/useAppDispatch';
import DetectionDrawer from './DetectionDrawer';
import DetectionDetailSections from './DetectionDetailSections';
import ScanLogPopupV2 from './ScanLogPopupV2';

export const SideDetailContext = createContext(null);

export default function SideDetailPanel({
	esFeed,
	onClose,
	expand = false,
	onExpand,
	setSearch,
	allCategories,
	showShimmer,
	setShowShimmer,
	refreshEndDate,
	previewOnly = false,
}) {
	const classes = useStyles();
	const customClasses = useDetectionStyles();
	const { toasts, triggerToast } = useToast();
	const { api } = useAppToolchain();
	const dispatch = useAppDispatch();

	const [imageSpecs, setImageSpecs] = React.useState({
		default: { width: 2550, height: 3300 }
	});
	const [showFBPopup, setShowFBPopup] = React.useState(false);
	const [showFalseNegativeFBPopup, setShowFalseNegativeFBPopup] = React.useState(false);
	const [showFBConfirm, setShowFBConfirm] = React.useState(false);
	const [showRedactions, setShowRedactions] = React.useState(true);
	const [visiblePreviews, setVisiblePreviews] = React.useState([]);
	const [classifierTags, setClassifierTags] = React.useState([]);
	const [feedBackFinding, setFeedbackFinding] = React.useState({});
	const loadedImageRef = useRef({});
	const preloadImageRef = useRef({});
	const [timeStamp, setTimeStamp] = React.useState(0);
	const [totalViolations, setTotalViolations] = React.useState(0);
	const [scrollIndex, setScrollIndex] = React.useState(0);
	const [validFindingFromRedaction, setValidFindingFromRedaction] = React.useState(false);
	const sharingClassifierRef = useRef([]);
	const [cropData, setCropData] = React.useState(null);
	const hideTimeoutRef = useRef(null);
	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

	const handleDrawerClose = () => {
		setIsDrawerOpen(false);
		// Also close the side detail panel
		if (onClose) {
			onClose();
		}
	};

	const handleDrawerExpand = () => {
		setIsDrawerOpen(false);
		onExpand(true);
		if (!previewOnly) {
			dispatch(setPageTitle('Detections Preview'));
		}
	};

	const pickFinding = (classifier, idx, zoom, currentPreviews) => {
		let counter = 0;
		let previews = [];

		currentPreviews.length > 0 ? previews = currentPreviews : previews = feed.previews;
		
		outerLoop: for (const [index, preview] of previews.entries()) {
			for (const [_, finding] of preview?.previewFindings.entries()) {
				if (finding.findingTag == classifier) {
					counter++;
					if (counter == idx) {
						let obj: any = getLocation(
							finding,
							zoom,
							preview.pixelDensity,
							imageSpecs[preview.id]?.width || imageSpecs.default.width,
							imageSpecs[preview.id]?.height || imageSpecs.default.height,
						);
						obj.border = showRedactions ? '3px solid #FFEB3B' : '3px solid #FCAD36';
						obj.borderRadius = '5px';
						obj.page = preview.page;
						setSelectedClassifierObj(obj);
						setPageNo(index + 1);
						if(scrollIndex !== index) {
							setScrollIndex(index);
						}
						break outerLoop;
					}
				}
			}
		}
	};

	const handleCopyClick = () => {
		navigator.clipboard.writeText('filePath : ' + esFeed.filePath.join('/'));

		triggerToast(
			{
			  type: 'success',
			  message: `File path copied.`,
			  duration: 3000,
			}
		  );
	};


	const getLocation = (
		previewFinding,
		zoom,
		dpi,
		imageWidth,
		imageHeight
	) => {
		const tl = previewFinding.topLeftCoordinate;
		const br = previewFinding.bottomRightCoordinate;
		const displayW = zoom * 6;
		const screenW = (displayW * dpi) / imageWidth;
		const width = (br.x - tl.x) * screenW + 6;
		const height = (br.y - tl.y) * screenW + 6;
		const left = tl.x * screenW - 3;
		const top = br.y * screenW + 3 - height;
		return { width, height, left, top };
	};

	const getTagContent = (tagText: string, count: number) => {
		if (tagText.length > 19) {
			return (
				<DIHtmlTooltip
					ttcontent={
						<Typography
							variant="body1"
							color="textSecondary"
							className={classNames(classes['d_inline'])}
						>
							<Typography
								variant="body1"
								key={tagText + ' ' + count}
								className={classNames(classes['p_1'])}
							>
								{tagText + ' ' + count}
							</Typography>
						</Typography>
					}
					content={
						<Typography
							variant="body1"
							color="textSecondary"
							className={classNames(
								classes['d_inline'],
								classes['cursor_pointer'],
							)}
						>
							{tagText.slice(0, 16) + '... ' + count}
						</Typography>
					}
				></DIHtmlTooltip>
			);
		} else {
			return tagText + ' ' + count;
		}
	};

	const myRef = useRef(null);
	const executeScroll = () => {
		if (myRef && myRef.current) {
			myRef.current.scrollIntoView();
		}
	};
	const [state, feed, err] = useApiRequest<Feed>(() => {
		setShowExternalUsers(false);
		setShowInternalUsers(false);
		setSelectedClassifier('');
		setSelectedClassifierObj(null);
		setClassiferTotal(0);
		setClassifierIdx(1);
		setZoom(100);
		setPageNo(1);
		setTimeStamp(new Date().getTime());
		return api.getFeedV2(esFeed.feedId, esFeed.subscriptionId);
	}, [esFeed]);

	// Open drawer immediately when feed is selected (not waiting for data to load)
	useEffect(() => {
		if (!expand) {
			setIsDrawerOpen(true);
		}
	}, [expand]);

	useEffect(() => {
		if (feed) {
			let tag = null;
			for (let i = 0; i < feed.tags.length; i++) {
				if (!isSharingClassifier(feed.tags[i].text)) {
					tag = feed.tags[i];
					break;
				}
			}
			if (tag != null) {
				setSelectedClassifier(tag.text);
				setClassiferTotal(tag.count);
				setVisiblePreviews(feed.previews.slice(0, -1).map((preview, index) => {
					if (index < 3) {
						loadedImageRef.current[preview.id] = true;
						return {
							...preview,
							redactedURL: `${preview.url}?access_token=${accessToken}&t=${timeStamp}`,
							revealedURL: `${preview.url}/reveal?access_token=${accessToken}&t=${timeStamp}`
						};
					} else {
						return { ...preview, url: '', redactedURL: '', revealedURL: '' };
					}

				}));
				setClassifierIdx(1);
				setClassifierTags(feed.tags.filter(tag => !isSharingClassifier(tag.text)));
				sharingClassifierRef.current = feed.tags.filter(tag => isSharingClassifier(tag.text));
				setTotalViolations(feed.tags.reduce((acc, curr) => {
					if(!isSharingClassifier(curr.text)) {
						acc += curr.count
					}
					return acc;
				}, 0));
				loadingDocumentWithAllPreviews(feed.previews, fullDocumentPreviewRef);
			}
		}

		return () => {
			setImageSpecs({
				default: { width: 2550, height: 3300 }
			});
		}
	}, [feed]);

	const isSharingClassifier = (text) => {
		return allCategories && allCategories['SharingClassifiers'].includes(text);
	}

	const generatePreviewWidthAndHeight = (width, height, id) => {
		if (!imageSpecs[id]) {
			setImageSpecs(prev => ({
				...prev,
				[id]: { width: width, height: height }
			}));
		}
	}

	const [showScanlog, setShowScanlog] = React.useState(false);
	const [zoom, setZoom] = React.useState(100);
	const [selectedClassifer, setSelectedClassifier] = React.useState('');
	const [selectedClassiferObj, setSelectedClassifierObj] = React.useState(null);
	const [classifierIdx, setClassifierIdx] = React.useState(1);
	const [classifierTotal, setClassiferTotal] = React.useState(0);
	const [showInternalUsers, setShowInternalUsers] = React.useState(false);
	const [showExternalUsers, setShowExternalUsers] = React.useState(false);
	const [pageNo, setPageNo] = React.useState(1);
	const [feedbackStatus, setFeedbackStatus] = React.useState('success');
	const [imageRefs, setImageRefs] = React.useState({});
	const [showPopOver, setShowPopOver] = React.useState(false);

	const [crop, setCrop] = React.useState(null);
	const [buttonPosition, setButtonPosition] = React.useState({ top: "0%", left: "0%" });
	const [cropEnabled, setCropEnabled] = React.useState(false);
	const [cropIndex, setCropIndex] = React.useState(null);
	const croppedImageRef = React.useRef(null);
	const fullDocumentClassifierRef = React.useRef({});
	const fullDocumentPreviewRef = React.useRef([]);
	const [isViewFullDocumentClicked, setIsViewFullDocumentClicked] = React.useState(false);
	const [isFullDocumentTooltipVisible, setIsFullDocumentTooltipVisible] = React.useState(false);

	const clearHidePopOverTimeout = () => {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
	};

	const schedulePopOverHide = () => {
		clearHidePopOverTimeout();
		hideTimeoutRef.current = setTimeout(() => {
			setShowPopOver(false);
		}, 200);
	};

	const handlePopOverShow = () => {
		clearHidePopOverTimeout();
		setShowPopOver(true);
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			clearHidePopOverTimeout();
			if (!previewOnly) {
				dispatch(setPageTitle('Detections'));
			}
		}

	}, []);

	useEffect(() => {
		executeScroll();
		updatingSelectedClassifierOnFullDocument(selectedClassifer, classifierIdx, fullDocumentPreviewRef.current, fullDocumentClassifierRef);
	}, [selectedClassifer, classifierIdx]);

	useEffect(() => {
		setSelectedClassifierObj(prev => ({ ...prev, border: showRedactions ? '3px solid #FFEB3B' : '3px solid #FCAD36' }));
	}, [showRedactions, visiblePreviews]);

	const tagsWithReport: Tag[] = feed
		? feed.tags.filter((t: Tag) => t.hasReport)
		: [];

	const [accessToken, setAccessToken] = React.useState('');
	useAPIEffect(() => {
		api.getAccessToken().then((access_token) => {
			setAccessToken(access_token);
		});
	});

	async function loadingDocumentWithAllPreviews(dataSource, fullDocumentPreviewRef) {
		if(isViewFullDocumentClicked) return;
		
		const firstPreviewData = dataSource[0];
		const totalPage = dataSource[dataSource.length - 1].page;
		let pointerIndex = 0;
	
		for (let page = 1; page <= totalPage; page++) {
			if (pointerIndex < dataSource.length - 1 && dataSource[pointerIndex].page === page) {
				fullDocumentPreviewRef.current.push(dataSource[pointerIndex]);
				pointerIndex++;
			} else {
				fullDocumentPreviewRef.current.push({
					expiration: firstPreviewData.expiration,
					id: `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
					isViewable: firstPreviewData.isViewable,
					page: page,
					pixelDensity: 300,
					previewFindings: [],
					url: `${firstPreviewData.url.split('/previews')[0]}/pages/${page}/download`,
					previewWithoutFindings: true,
				});
			}
		}
	}

	async function updatingSelectedClassifierOnFullDocument(classifier, idx, previews = [], fullDocumentClassifierRef) {
		if(isViewFullDocumentClicked) return;

		let counter = 0;
		
		outerLoop: for (const [index, preview] of previews.entries()) {
			for (const [_, finding] of preview?.previewFindings.entries()) {
				if (finding.findingTag == classifier) {
					counter++;
					if (counter == idx) {
						fullDocumentClassifierRef.current = {
							previewIndex: index,
							page: index + 1,
							classifier
						}
						break outerLoop;
					}
				}
			}
		}
	}

	function handleFullDocumentClick(classifierObj, previews, loadedImageRef, setImageRefs, showRedactions) {
		if(isViewFullDocumentClicked) return;

		setIsViewFullDocumentClicked(true);
		loadedImageRef.current = {};
		preloadImageRef.current = {};
		const timeStamp = new Date().getTime();
		setTimeStamp(timeStamp);
		const currentIndex = classifierObj.previewIndex;

		setVisiblePreviews(previews.map((preview, index) => {
			const baseUrl = `${preview.url}?access_token=${accessToken}&t=${timeStamp}`;
			if (index === currentIndex || index === currentIndex - 1 || index === currentIndex + 1) {
				if (index >= 0 && index < previews.length) {
					loadedImageRef.current[preview.id] = true;
					const revealedUrl = preview?.previewWithoutFindings 
						? baseUrl 
						: `${preview.url}/reveal?access_token=${accessToken}&t=${timeStamp}`;

					preloadImage(showRedactions, baseUrl, revealedUrl);
					return {
						...preview,
						redactedURL: baseUrl,
						revealedURL: revealedUrl,
					};
				}
			}

			return { ...preview, url: '', redactedURL: '', revealedURL: '' };	
		}));

		setImageRefs(
			previews.reduce((acc, value, index) => {
				acc[index] = React.createRef();
				return acc;
			}, {})
		);

		pickFinding(selectedClassifer, classifierIdx, zoom, previews);
	}

	const preloadImage = (showRedactions, baseUrl, revealedURL) => {
        const img = new Image();
        img.src = !showRedactions ? `${baseUrl}` : `${revealedURL}`;
	};

	function resetCropAndFullDocumentState() {
		fullDocumentClassifierRef.current = {}
		fullDocumentPreviewRef.current =[];
		croppedImageRef.current = {};
		setVisiblePreviews([]);
		setIsViewFullDocumentClicked(false);
		setCropIndex(null);
		setCropEnabled(false);
	}

	const contextValue = {
		feedBackFinding,
		setFeedbackFinding,
		visiblePreviews,
		setVisiblePreviews,
		classifierTags,
		setClassifierTags,
		classifierIdx,
		setClassifierIdx,
		classifierTotal,
		setClassiferTotal,
		selectedClassifer,
		setSelectedClassifier,
		selectedClassiferObj,
		setSelectedClassifierObj,
		feedbackStatus,
		setFeedbackStatus,
		setShowFBPopup,
		setShowFBConfirm,
		timeStamp,
		pickFinding,
		pageNo,
		setPageNo,
		accessToken,
		zoom,
		myRef,
		imageSpecs,
		showRedactions,
		getLocation,
		imageRefs,
		setImageRefs,
		refreshEndDate,
		totalViolations,
		setTotalViolations,
		onExpand,
		onClose,
		scrollIndex,
		setScrollIndex,
		isSharingClassifier,
		validFindingFromRedaction,
		setValidFindingFromRedaction,
		sharingClassifierRef,
		setShowPopOver,
		crop,
		setCrop,
		buttonPosition,
		setButtonPosition,
		cropEnabled,
		setCropEnabled,
		cropIndex,
		setCropIndex,
		setShowFalseNegativeFBPopup,
		cropData,
		setCropData,
		croppedImageRef,
		isViewFullDocumentClicked,
		fullDocumentPreviewRef,
		subscriptionId: esFeed.subscriptionId,
		handlePopOverShow,
		schedulePopOverHide,
	};

	if (previewOnly) {
		return (
			<SideDetailContext.Provider value={contextValue}>
				<Dialog
					open={true}
					onClose={onClose}
					maxWidth={false}
					PaperProps={{
						style: {
							width: '80vw',
							height: '80vh',
							maxWidth: '80vw',
						},
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '12px 16px',
							borderBottom: '1px solid #e0e0e0',
						}}
					>
						<Typography variant="h4" color="textPrimary" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
							{esFeed.fileName}
						</Typography>
						<IconButton aria-label="Close" onClick={onClose} size="small">
							<Close />
						</IconButton>
					</Box>
					<Box sx={{ flexGrow: 1, overflow: 'auto' }}>
						{err ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
								<Typography color="error">Failed to load preview. Please try again later.</Typography>
							</Box>
						) : feed && accessToken !== '' ? (
							<ImageViewer
								viewingPage={(pageNo) => setPageNo(pageNo)}
								previews={feed.previews.slice(0, -1)}
								zoom={zoom}
								selectedClassiferObj={selectedClassiferObj}
								myRef={myRef}
								accessToken={accessToken}
								generatePreviewWidthAndHeight={generatePreviewWidthAndHeight}
								visiblePreviews={visiblePreviews}
								setVisiblePreviews={setVisiblePreviews}
								isViewable={feed.isPdfAvailable}
								setSelectedClassifierObj={setSelectedClassifierObj}
								getLocation={getLocation}
								selectedClassifer={selectedClassifer}
								loadedImageRef={loadedImageRef}
								preloadImageRef={preloadImageRef}
								imageSpecs={imageSpecs}
								classifierIdx={classifierIdx}
								setShowFBPopup={setShowFBPopup}
							/>
						) : (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
								<Typography color="textSecondary">Loading preview...</Typography>
							</Box>
						)}
					</Box>
				</Dialog>
			</SideDetailContext.Provider>
		);
	}

	return (
		<BDSProvider>
			<SideDetailContext.Provider value={contextValue}>
				<div>
					<FeedbackConfirm
						show={showFBConfirm}
						closePopup={() => {
							setShowFBConfirm(false);
						}}
					></FeedbackConfirm>
					<FeedbackPopup
						show={showFBPopup}
						closePopup={(status) => {
							if (status) setShowFBConfirm(true);
							setShowFBPopup(false);
						}}
					></FeedbackPopup>
					<FalseNegativeFeedbackPopup
						show={showFalseNegativeFBPopup}
						closePopup={(status) => {
							if (status) setShowFBConfirm(true);
							setShowFalseNegativeFBPopup(false);
						}}
					></FalseNegativeFeedbackPopup>

					{expand && (
						<ScanLogPopupV2
							showScanlog={showScanlog}
							searchText={esFeed.fileName}
							itemId={esFeed.itemId}
							closePopup={() => {
								setShowScanlog(false);
							}}
						/>
					)}

					{/* Detection Drawer for preview mode */}
					{!expand && (
						<DetectionDrawer
							open={isDrawerOpen}
							onClose={handleDrawerClose}
							onExpand={handleDrawerExpand}
							feed={feed}
							accessToken={accessToken}
							timeStamp={timeStamp}
							showShimmer={showShimmer}
							setShowShimmer={setShowShimmer}
							imageSpecs={imageSpecs}
							generatePreviewWidthAndHeight={
								generatePreviewWidthAndHeight
							}
							selectedClassifer={selectedClassifer}
							pickFinding={pickFinding}
							zoom={zoom}
							visiblePreviews={visiblePreviews}
							classifierTags={classifierTags}
							isDrawerLoading={!feed}
							setSearch={setSearch}
							esFeed={esFeed}
							setShowInternalUsers={setShowInternalUsers}
							setShowExternalUsers={setShowExternalUsers}
							showExternalUsers={showExternalUsers}
							handleCopyClick={handleCopyClick}
							tagsWithReport={tagsWithReport}
							showInternalUsers={showInternalUsers}
						/>
					)}

					{expand && feed && (
						<div
							className={classNames(
								classes['border_bottom_gray'],
							)}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
							>
								<Typography
									variant="h4"
									color="textPrimary"
									style={{
										maxWidth: expand ? 1200 : 340,
										textOverflow: 'ellipsis',
										overflow: 'hidden',
									}}
								>
									{esFeed.fileName}
								</Typography>

								<Box sx={{ flexGrow: 1 }}></Box>
								<IconButton
									sx={{marginRight: '12px'}}
									aria-label="Close"
									onClick={() => {
										onClose();
										if (expand) {
											onExpand(false);
											refreshEndDate();
											setShowRedactions(true);
										}
										resetCropAndFullDocumentState();
									}}
									edge="end"
								>
									<Close />
								</IconButton>
							</Box>
						</div>
					)}
					{feed != undefined && state == 'RESOLVED' && expand ? (
						<div
							className={
								customClasses[
									'detection-sidePanel-preview-container'
								]
							}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
								}}
							>
								{expand ? (
									<>
										<Box
											sx={{
												flexGrow: 1,
											}}
											className={classNames(
												classes['bg_black'],
												customClasses['pos-rel'],
											)}
										>
											<div>
												<ImageToolbox
													classifier={
														selectedClassifer
													}
													total={classifierTotal}
													current={classifierIdx}
													onDown={() => {
														let idx =
															(classifierIdx +
																1) %
															classifierTotal;
														if (idx == 0)
															idx =
																classifierTotal;
														setClassifierIdx(idx);
														pickFinding(
															selectedClassifer,
															idx,
															zoom,
															visiblePreviews,
														);
													}}
													onUp={() => {
														const idx =
															classifierIdx > 1
																? classifierIdx -
																  1
																: classifierTotal;

														setClassifierIdx(idx);
														pickFinding(
															selectedClassifer,
															idx,
															zoom,
															visiblePreviews,
														);
													}}
													onZoomIn={() => {
														if (zoom < 300) {
															setZoom(zoom + 10);
															pickFinding(
																selectedClassifer,
																classifierIdx,
																zoom + 10,
																visiblePreviews,
															);
														}
													}}
													onZoomOut={() => {
														if (zoom > 10) {
															setZoom(zoom - 10);
															pickFinding(
																selectedClassifer,
																classifierIdx,
																zoom - 10,
																visiblePreviews,
															);
														}
													}}
													zoom={zoom}
													setShowRedactions={
														setShowRedactions
													}
													isViewable={
														feed.isPdfAvailable
													}
													isCropEnabled={cropEnabled}
												></ImageToolbox>
											</div>

											<div
												className={
													customClasses[
														'preview-img-container'
													]
												}
											>
												{accessToken != '' ? (
													<ImageViewer
														viewingPage={(
															pageNo,
														) => {
															setPageNo(pageNo);
														}}
														previews={
															!isViewFullDocumentClicked
																? feed.previews.slice(
																		0,
																		-1,
																  )
																: fullDocumentPreviewRef.current
														}
														zoom={zoom}
														selectedClassiferObj={
															selectedClassiferObj
														}
														myRef={myRef}
														accessToken={
															accessToken
														}
														generatePreviewWidthAndHeight={
															generatePreviewWidthAndHeight
														}
														visiblePreviews={
															visiblePreviews
														}
														setVisiblePreviews={
															setVisiblePreviews
														}
														isViewable={
															feed.isPdfAvailable
														}
														setSelectedClassifierObj={
															setSelectedClassifierObj
														}
														getLocation={
															getLocation
														}
														selectedClassifer={
															selectedClassifer
														}
														loadedImageRef={
															loadedImageRef
														}
														preloadImageRef={
															preloadImageRef
														}
														imageSpecs={imageSpecs}
														classifierIdx={
															classifierIdx
														}
														setShowFBPopup={
															setShowFBPopup
														}
													></ImageViewer>
												) : null}
											</div>

											{!cropEnabled &&
												feed.isPdfAvailable &&
												showPopOver && (
													<div
														id="false-negative-popover"
														className={
															customClasses[
																'preview-img-btns-container'
															]
														}
														onMouseEnter={
															handlePopOverShow
														}
														onMouseLeave={
															schedulePopOverHide
														}
													>
														<div
															className={classNames(
																classes[
																	'flex_ver_center'
																],
																customClasses[
																	'gap_20'
																],
															)}
														>
															<Button
																bdsType="interactiveSubtle"
																className={classNames(
																	classes[
																		'flex_ver_center'
																	],
																)}
																sx={{
																	color: '#FFFFFF',
																	fontWeight: 700,
																	fontSize:
																		'16px',
																	textTransform:
																		'none',
																}}
																startIcon={
																	<FeedbackIcon
																		sx={{
																			color: '#067CC1',
																		}}
																	/>
																}
																onClick={() => {
																	setCropEnabled(
																		true,
																	);
																	setCropIndex(
																		pageNo -
																			1,
																	);
																}}
															>
																Report Issue
															</Button>

															<Tooltip
																title="The full document is already loaded."
																placement="top"
																open={
																	isFullDocumentTooltipVisible
																}
																disableFocusListener
																disableTouchListener
																slotProps={{
																	tooltip: {
																		sx: {
																			backgroundColor:
																				'#191919',
																		},
																	},
																}}
															>
																<span>
																	<Button
																		bdsType="interactiveSubtle"
																		className={classNames(
																			classes[
																				'flex_ver_center'
																			],
																		)}
																		sx={{
																			color: isViewFullDocumentClicked
																				? '#7D7D7D61'
																				: '#FFFFFF',
																			fontWeight: 700,
																			fontSize:
																				'16px',
																			textTransform:
																				'none',
																			cursor: isViewFullDocumentClicked
																				? 'default'
																				: 'pointer',
																		}}
																		startIcon={
																			<TextSnippetIcon
																				sx={{
																					color: isViewFullDocumentClicked
																						? '#7D7D7D61'
																						: '#067CC1',
																				}}
																			/>
																		}
																		onMouseEnter={() => {
																			if (
																				isViewFullDocumentClicked
																			)
																				setIsFullDocumentTooltipVisible(
																					true,
																				);
																		}}
																		onMouseLeave={() =>
																			setIsFullDocumentTooltipVisible(
																				false,
																			)
																		}
																		onClick={() =>
																			handleFullDocumentClick(
																				fullDocumentClassifierRef.current,
																				fullDocumentPreviewRef.current,
																				loadedImageRef,
																				setImageRefs,
																				showRedactions,
																			)
																		}
																	>
																		View
																		full
																		document
																	</Button>
																</span>
															</Tooltip>
														</div>
													</div>
												)}
										</Box>
										<Box
											sx={{
												width: 200,
												height: 'calc(100vh - 150px)',
												display: 'flex',
												flexDirection: 'column',
												overflow: 'auto',
											}}
											className={classNames(
												classes['bg_white'],
												classes['border_right_gray'],
											)}
										>
											<section
												className={classNames(
													!cropEnabled
														? classes['d_visible']
														: classes['d_hidden'],
												)}
											>
												<div
													className={classNames(
														classes['p_2'],
													)}
												>
													<Typography
														variant="h4"
													>
														Classifiers
													</Typography>
													<Typography
														color="secondary"
														fontSize={12}
														component="span"
													>
														Choose classifiers, and
														then utilize the arrows
														in the top toolbar to
														navigate through the
														document, allowing you
														to view them seamlessly
														within the text.
													</Typography>
												</div>
												{classifierTags.map((tag) => {
													if (
														allCategories &&
														!allCategories[
															'SharingClassifiers'
														].includes(tag.text)
													) {
														return (
															<div
																key={tag.text}
																onClick={() => {
																	if (
																		tag.text !=
																		selectedClassifer
																	) {
																		setSelectedClassifier(
																			tag.text,
																		);
																		setClassiferTotal(
																			tag.count,
																		);
																		setClassifierIdx(
																			1,
																		);
																		pickFinding(
																			tag.text,
																			1,
																			zoom,
																			visiblePreviews,
																		);
																	}
																}}
																style={{
																	backgroundColor:
																		tag.text ==
																		selectedClassifer
																			? '#E4F4FF'
																			: '#FFFFFF',
																	padding: 8,
																	cursor: 'pointer',
																}}
															>
																<div
																	className={
																		customClasses[
																			'flex-center'
																		]
																	}
																>
																	<Chip
																		color="primary"
																		label={getTagContent(
																			tag.text,
																			tag.count,
																		)}
																		size="small"
																		style={{
																			...getTagStyle(
																				tag.text,
																			)
																				.tagStyle,
																			cursor: 'pointer',
																		}}
																	/>
																	{tag.text ==
																	selectedClassifer ? (
																		<CheckIcon
																			color="primary"
																			fontSize="small"
																			className={classNames(
																				classes[
																					'ml_2'
																				],
																			)}
																		></CheckIcon>
																	) : null}
																</div>
															</div>
														);
													}
												})}
											</section>
											<Box sx={{ flexGrow: 1 }}></Box>
											{feed?.isPdfAvailable &&
												!cropEnabled && (
													<Box
														className={classNames(
															classes['mb_2'],
															classes['ml_2'],
														)}
													>
														<Box
															sx={{
																color: '#8E8D8D',
															}}
														>
															<InfoIcon />
														</Box>
														<Typography
															color="secondary"
															component="span"
														>
															{showRedactions
																? 'Turn off the redaction switch and click on a detection to report incorrect classifications.'
																: 'Click on classifiers to improve future detection accuracy and remove the selected detection.'}
														</Typography>
													</Box>
												)}

											<Typography
												color={'secondary'}
												component="span"
												marginLeft='16px'
											>
												Redacted page {pageNo} of{' '}
												{visiblePreviews.length}
											</Typography>
										</Box>
									</>
								) : null}
								<Box
									sx={{
										width: 416,
										paddingLeft: '16px !important',
										height: 'calc(100vh - 150px)',
										overflow: 'auto',
									}}
								>
									<DetectionDetailSections
										esFeed={esFeed}
										feed={feed}
										setSearch={setSearch}
										handleCopyClick={handleCopyClick}
										onViewScanLog={() =>
											setShowScanlog(true)
										}
										showInternalUsers={showInternalUsers}
										setShowInternalUsers={
											setShowInternalUsers
										}
										showExternalUsers={showExternalUsers}
										setShowExternalUsers={
											setShowExternalUsers
										}
										tagsWithReport={tagsWithReport}
									/>
								</Box>
							</Box>
						</div>
					) : null}
				</div>

				<ToastComponent toasts={toasts} position={'bottom-left'} />
			</SideDetailContext.Provider>
		</BDSProvider>
	);
}

